import { get, type ReqConfig } from '../request';
import { getToken } from '../store/auth';

/** 后端菜单节点（GET /auth/menus） */
export interface AuthMenu {
  code: string;
  name: string;
  type: string;
  parent_code?: string;
  route?: string;
  sort?: number;
  children?: AuthMenu[];
}

/** GET /auth/me 返回的 RBAC 上下文 */
export interface MeInfo {
  user_id: number;
  username: string;
  roles: string[];
  permissions: string[];
  org_id: number;
  org_scope: string;
}

/** 引导类请求失败由「权限信息加载失败」错误态承载（单一提示），故调用方传 silent 抑制通用 toast */
export function fetchMe(cfg?: ReqConfig): Promise<MeInfo> {
  return get('/auth/me', undefined, cfg) as unknown as Promise<MeInfo>;
}

export function fetchMenus(cfg?: ReqConfig): Promise<AuthMenu[]> {
  return get('/auth/menus', undefined, cfg) as unknown as Promise<AuthMenu[]>;
}

/** GET /introspect 的响应（后端只回「活着 + 你是谁」，不查库） */
export interface IntrospectInfo {
  active: boolean;
  user_id?: number;
  sid?: string;
  role?: string;
  exp?: number;
}

/**
 * 本机 token 现在还活着吗（GET /auth/introspect；宿主系统逐请求回查用的同一个端点）。
 *
 * 走裸 fetch 而不走 request.ts 的 get()：那套拦截器对 401 的处理是**清本地登录态 + 整页跳 /login**，
 * 而这里的语义恰好是「先问一句，再决定要不要提示重新登录」——探测本身绝不能把坐席踢下线。
 * 只在 401/403 等 HTTP 失败时返回 false；网络/超时异常照常抛出，由调用方判断「不知道」。
 */
export async function introspectToken(): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  const resp = await fetch('/api/v1/introspect', {
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
  });
  if (!resp.ok) return false;
  try {
    const body = (await resp.json()) as { data?: IntrospectInfo };
    return !!body?.data?.active;
  } catch {
    return false;
  }
}
